/**
 * Presentational step bodies for the create-process wizard: Type (OptionGrid),
 * Details (reason + date + endpoints + items), and Review. State is owned by the
 * parent `CapacityProcessNewPage`; these are pure render + callbacks.
 */
import { Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  Textarea,
  OptionGridSelector,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import type { Item, ImportItemMetadata, ProcessType } from '@/api/capacities';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { processTypeLabel } from '../process-format';
import { formatWhCurrency } from '../../shared/wh-format';
import { PROCESS_ENDPOINT_MAP, PROCESS_TYPE_OPTIONS } from './process-type-options';
import { EndpointSelect } from './endpoint-select';
import { ImportItemForm } from './import-item-form';
import {
  endpointRequirements,
  isImportType,
  itemCount,
  type WizardErrors,
  type WizardState,
} from './create-payload';
import { DateInput } from '@/components/date-input';

export type { WizardState, WizardErrors } from './create-payload';
export {
  buildCreatePayload,
  buildUpdatePayload,
  canSubmit,
  endpointRequirements,
  processToWizardState,
  validateWizard,
} from './create-payload';

/* ── Step 1: type ──────────────────────────────────────────────────────── */
export function TypeStep({
  value,
  onChange,
  disabled = false,
}: {
  value?: ProcessType;
  onChange: (v: ProcessType) => void;
  /** Edit mode: the process type is fixed after creation. */
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {disabled ? (
        <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          The process type is fixed after creation and cannot be changed.
        </div>
      ) : null}
      <OptionGridSelector
        options={PROCESS_TYPE_OPTIONS}
        value={value ?? null}
        onChange={onChange}
        minColWidth={240}
        disabled={disabled}
        aria-label="Process type"
      />
    </div>
  );
}

/* ── Step 2: details ───────────────────────────────────────────────────── */
export function DetailsStep({
  state,
  onField,
  onOpenItems,
  onRemoveItem,
  onAddImportItem,
  onRemoveImportItem,
  errors = {},
}: {
  state: WizardState;
  onField: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
  onOpenItems: () => void;
  onRemoveItem: (id: string) => void;
  onAddImportItem: (item: ImportItemMetadata) => void;
  onRemoveImportItem: (index: number) => void;
  /** Validation errors shown once the user has attempted to advance. */
  errors?: WizardErrors;
}) {
  const endpoints = state.type ? PROCESS_ENDPOINT_MAP[state.type] : {};
  const req = endpointRequirements(state.type);
  const isImport = isImportType(state.type);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--vn-space-4)',
        }}
      >
        <Field label="Process date" required error={errors.processDate}>
          {(p) => (
            <DateInput
              {...p}
              value={state.processDate}
              onChange={(e) => onField('processDate', e.target.value)}
            />
          )}
        </Field>
        <Field label="Expected date">
          {(p) => (
            <DateInput
              {...p}
              value={state.expectedDate}
              onChange={(e) => onField('expectedDate', e.target.value)}
            />
          )}
        </Field>
      </div>

      <Field label="Reason" required error={errors.reason}>
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            value={state.reason}
            onChange={(e) => onField('reason', e.target.value)}
            placeholder="Why is this process being created?"
          />
        )}
      </Field>

      <Field label="Description">
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            value={state.description}
            onChange={(e) => onField('description', e.target.value)}
            placeholder="Additional details (optional)"
          />
        )}
      </Field>

      <Field label="Notes">
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            value={state.notes}
            onChange={(e) => onField('notes', e.target.value)}
            placeholder="Internal notes (optional)"
          />
        )}
      </Field>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--vn-space-4)',
        }}
      >
        {endpoints.source ? (
          <EndpointSelect
            label="Source"
            kind={endpoints.source}
            value={state.source}
            required={req.source}
            error={errors.source}
            onChange={(v, name) => {
              onField('source', v);
              onField('sourceName', name);
            }}
          />
        ) : null}
        {endpoints.destination ? (
          <EndpointSelect
            label="Destination"
            kind={endpoints.destination}
            value={state.destination}
            required={req.destination}
            error={errors.destination}
            onChange={(v, name) => {
              onField('destination', v);
              onField('destinationName', name);
            }}
          />
        ) : null}
      </div>

      {isImport ? (
        <ImportItemsSection
          items={state.importItems}
          onAdd={onAddImportItem}
          onRemove={onRemoveImportItem}
        />
      ) : (
        <ItemsPicker items={state.items} onOpenItems={onOpenItems} onRemoveItem={onRemoveItem} />
      )}
    </div>
  );
}

/** Import types: define NEW items (created on approval) instead of picking inventory. */
function ImportItemsSection({
  items,
  onAdd,
  onRemove,
}: {
  items: ImportItemMetadata[];
  onAdd: (item: ImportItemMetadata) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        Add new items to import. Items will be created in the system when this process is approved.
      </div>
      <ImportItemForm onAdd={onAdd} />
      {items.length === 0 ? (
        <EmptyState icon={<Plus size={20} />} title="No items to import" description="Define at least one item above." />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--vn-space-1)' }}>
          {items.map((it, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--vn-space-2)',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
                border: '1px solid var(--vn-border-subtle)',
                borderRadius: 'var(--vn-radius-md)',
              }}
            >
              <span style={{ overflowWrap: 'anywhere' }}>
                {it.name}
                <span style={{ color: 'var(--vn-fg-muted)' }}>
                  {' · '}
                  {it.category} ·{' '}
                  {it.service_tags?.length ? `${it.service_tags.length} tag(s)` : `×${it.quantity ?? 1}`}
                </span>
              </span>
              <Button size="sm" variant="ghost" aria-label={`Remove ${it.name}`} onClick={() => onRemove(i)}>
                <X size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemsPicker({
  items,
  onOpenItems,
  onRemoveItem,
}: {
  items: Item[];
  onOpenItems: () => void;
  onRemoveItem: (id: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          Items ({items.length})
        </span>
        <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} onClick={onOpenItems}>
          Add items
        </Button>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={<Trash2 size={20} />} title="No items added" description="Add at least one item." />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--vn-space-1)' }}>
          {items.map((it) => (
            <li
              key={it.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--vn-space-2)',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
                border: '1px solid var(--vn-border-subtle)',
                borderRadius: 'var(--vn-radius-md)',
              }}
            >
              <span style={{ overflowWrap: 'anywhere' }}>
                {it.name}
                {it.service_tag || it.ref ? (
                  <span style={{ color: 'var(--vn-fg-muted)' }}> · {it.service_tag || it.ref}</span>
                ) : null}
              </span>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${it.name}`}
                onClick={() => onRemoveItem(it.id)}
              >
                <X size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Step 3: review ────────────────────────────────────────────────────── */
export function ReviewStep({ state }: { state: WizardState }) {
  const isImport = isImportType(state.type);
  const summary: InfoItem[] = [
    { label: 'Type', value: processTypeLabel(state.type) },
    { label: 'Reason', value: state.reason || '—', full: true },
    { label: 'Process date', value: state.processDate || '—' },
    { label: 'Expected date', value: state.expectedDate || '—' },
    { label: 'Source', value: state.sourceName || state.source || '—' },
    { label: 'Destination', value: state.destinationName || state.destination || '—' },
    {
      label: isImport ? 'Items to import' : 'Items',
      value: `${itemCount(state)} item(s)`,
    },
    ...(state.description ? [{ label: 'Description', value: state.description, full: true }] : []),
    ...(state.notes ? [{ label: 'Notes', value: state.notes, full: true }] : []),
  ];
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <InfoGrid items={summary} />
      <ReviewItemsTable state={state} isImport={isImport} />
    </div>
  );
}

type ReviewRow = { name: string; category: string; qty: string; extra: string; price: string };

/** Line-item list on the Review step — the actual items, not just a count. */
function ReviewItemsTable({ state, isImport }: { state: WizardState; isImport: boolean }) {
  const rows: ReviewRow[] = isImport
    ? state.importItems.map((it) => ({
        name: it.name,
        category: it.category,
        qty: it.service_tags?.length ? `${it.service_tags.length} tag(s)` : `×${it.quantity ?? 1}`,
        extra: it.condition ?? '—',
        price: formatWhCurrency(it.purchase_price),
      }))
    : state.items.map((it) => ({
        name: it.name,
        category: it.category ?? '—',
        qty: it.service_tag || it.ref || '—',
        extra: it.condition ?? '—',
        price: formatWhCurrency(it.purchase_price),
      }));

  if (rows.length === 0) return null;

  const reviewColumns: ColumnDef<ReviewRow, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>,
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.category}</span>,
    },
    { id: 'qty', header: isImport ? 'Qty / Tags' : 'Tag / Ref', accessorKey: 'qty' },
    {
      id: 'extra',
      header: 'Condition',
      cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.extra}</span>,
    },
    {
      id: 'price',
      header: 'Price',
      cell: ({ row }) => <span style={{ whiteSpace: 'nowrap' }}>{row.original.price}</span>,
    },
  ];

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--vn-font-size-sm)',
          fontWeight: 'var(--vn-font-weight-medium)',
          marginBottom: 'var(--vn-space-2)',
        }}
      >
        {isImport ? 'Items to import' : 'Items'} ({rows.length})
      </div>
      <DataTable columns={reviewColumns} data={rows} enableSorting={false} />
    </div>
  );
}
